import { useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Facebook, Instagram, Youtube } from "lucide-react";
import { SettingsContext } from "@/App";
import AfricaMap from "./AfricaMap";
import { useSiteNavigation, pageLabel } from "@/hooks/useSiteNavigation";

function TikTokIcon({ size = 16 }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

export default function Footer() {
  const { settings } = useContext(SettingsContext);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const footerPages = useSiteNavigation("footer");

  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/moderator")) return null;

  const socialIcons = [
    { key: "facebook", url: settings?.social_facebook, Icon: Facebook },
    { key: "youtube", url: settings?.social_youtube, Icon: Youtube },
    { key: "instagram", url: settings?.social_instagram, Icon: Instagram },
    { key: "tiktok", url: settings?.social_tiktok, Icon: TikTokIcon },
  ];

  return (
    <footer
      className="relative w-full border-t border-white/[0.07]"
      style={{
        background: "linear-gradient(180deg, #060d1a 0%, #0a1628 100%)",
      }}
      data-testid="footer"
    >
      <div className="w-full max-w-[1440px] mx-auto px-6 sm:px-8 pt-8 pb-6">
        {/* 6-column grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-y-8 gap-x-8 xl:gap-x-10" data-testid="footer-grid">

          {/* Col 1: Brand + Social */}
          <div className="lg:col-span-1 sm:col-span-2 lg:col-span-1" style={{ minWidth: 0 }}>
            <Link to="/" data-testid="footer-logo">
              <img
                src="/djilu-logo.png"
                alt="DJILU.COM"
                className="h-10 sm:h-11 w-auto object-contain mb-3"
              />
            </Link>
            <p className="text-[#8E99AA] text-[13px] leading-[1.7] mb-4">
              {t("footer.description")}
            </p>
            <div className="flex items-center gap-2.5">
              {socialIcons.map(({ key, url, Icon }) => (
                <a
                  key={key}
                  href={url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-${key}`}
                  className="w-9 h-9 rounded-full bg-white/[0.06] flex items-center justify-center text-[#A1A9B8] hover:bg-[#FFC928] hover:text-black transition-all duration-200"
                >
                  <Icon size={15} />
                </a>
              ))}
            </div>
          </div>

          {/* Col 2: Navigation */}
          <div>
            <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-[0.12em]">{t("footer.navigation")}</h4>
            <ul className="space-y-2.5">
              {[
                { label: t("footer.home"), path: "/" },
                { label: t("footer.liveTv"), path: "/live" },
                { label: t("footer.channels"), path: "/?view=categories" },
                { label: t("footer.categories"), path: "/categories" },
                { label: t("footer.countries"), path: "/countries" },
              ].map(link => (
                <li key={link.path + link.label}>
                  <Link to={link.path} className="text-[#A1A9B8] text-[13px] hover:text-[#FFC928] transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Informations */}
          <div>
            <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-[0.12em]">{t("footer.information")}</h4>
            <ul className="space-y-2.5">
              {[
                { label: t("footer.about"), path: "/about" },
                { label: t("footer.subscription"), path: "/membership" },
                { label: t("footer.terms"), path: "/terms" },
                { label: t("footer.privacy"), path: "/privacy" },
                { label: t("footer.faq"), path: "/faq" },
              ].map(link => (
                <li key={link.path}>
                  <Link to={link.path} className="text-[#A1A9B8] text-[13px] hover:text-[#FFC928] transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
              {footerPages.map(p => (
                <li key={p.slug} data-testid={`footer-page-${p.slug}`}>
                  {p.new_tab ? (
                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-[#A1A9B8] text-[13px] hover:text-[#FFC928] transition-colors">{pageLabel(p, i18n)}</a>
                  ) : (
                    <Link to={p.url} className="text-[#A1A9B8] text-[13px] hover:text-[#FFC928] transition-colors">{pageLabel(p, i18n)}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Col 4: Support */}
          <div>
            <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-[0.12em]">{t("footer.support")}</h4>
            <ul className="space-y-2.5">
              {[
                { label: t("footer.helpSupport"), path: "/support" },
                { label: t("footer.contactUs"), path: "/contact" },
                { label: t("footer.reportProblem"), path: "/report" },
                { label: t("footer.submitChannel"), path: "/submit-channel" },
              ].map(link => (
                <li key={link.path}>
                  <Link to={link.path} className="text-[#A1A9B8] text-[13px] hover:text-[#FFC928] transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 5: Download App */}
          <div>
            <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-[0.12em]">{t("footer.downloadApp")}</h4>
            <div className="flex flex-col gap-2.5">
              {/* Google Play */}
              <a href="#" className="flex items-center gap-2.5 pl-3 pr-4 py-2 bg-white/[0.04] border border-white/[0.08] rounded-xl hover:border-[#FFC928]/30 transition-colors" data-testid="google-play-btn">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" className="flex-shrink-0">
                  <path d="M3 20.5V3.5C3 2.91 3.34 2.39 3.84 2.15L13.69 12L3.84 21.85C3.34 21.6 3 21.09 3 20.5Z" fill="#4CAF50"/>
                  <path d="M16.81 15.12L6.05 21.34L13.69 12L16.81 15.12Z" fill="#F44336"/>
                  <path d="M20.16 10.81C20.5 11.08 20.5 11.92 20.16 12.19L17.89 13.5L14.5 12L17.89 10.5L20.16 10.81Z" fill="#FFC107"/>
                  <path d="M6.05 2.66L16.81 8.88L13.69 12L6.05 2.66Z" fill="#2196F3"/>
                </svg>
                <div>
                  <div className="text-[9px] text-[#8E99AA] leading-none">{t("footer.availableOn")}</div>
                  <div className="text-white text-[13px] font-semibold leading-tight">Google Play</div>
                </div>
              </a>
              {/* App Store */}
              <a href="#" className="flex items-center gap-2.5 pl-3 pr-4 py-2 bg-white/[0.04] border border-white/[0.08] rounded-xl hover:border-[#FFC928]/30 transition-colors" data-testid="app-store-btn">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="white" className="flex-shrink-0">
                  <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 16.56 2.93 11.3 4.7 7.72C5.57 5.94 7.36 4.86 9.28 4.84C10.56 4.82 11.78 5.72 12.57 5.72C13.36 5.72 14.82 4.62 16.39 4.82C17.07 4.85 18.94 5.09 20.13 6.83C20.04 6.89 17.8 8.18 17.83 10.97C17.87 14.32 20.67 15.41 20.7 15.42C20.67 15.5 20.18 17.18 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
                </svg>
                <div>
                  <div className="text-[9px] text-[#8E99AA] leading-none">{t("footer.downloadOn")}</div>
                  <div className="text-white text-[13px] font-semibold leading-tight">App Store</div>
                </div>
              </a>
            </div>
          </div>

          {/* Col 6: Africa Map */}
          <div className="flex justify-center lg:justify-end items-start">
            <AfricaMap className="w-[200px] h-[180px] lg:w-[230px] lg:h-[200px]" />
          </div>
        </div>
      </div>

      {/* Copyright Row */}
      <div className="border-t border-white/[0.06]">
        <div className="w-full max-w-[1440px] mx-auto px-6 sm:px-8 flex items-center justify-center min-h-[54px]">
          <p className="text-[#7f8999] text-xs" data-testid="footer-copyright">
            {t("footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
}
